<template>
	<div class="row">
		<div class="col-12 d-flex flex-row align-items-center justify-content-between">
			<h1>{{ $t('language-overrides') }}</h1>
			<button @click="save" class="btn btn-primary mb-3" :disabled="submitting || !dirty">{{ $t('save') }}</button>
		</div>

		<div class="col-12">
			<p>{{ $t('language-overrides-description') }}</p>

			<SelectInput
				:label="$t('language-file')"
				v-model="selectedLocale"
				:options="locales"
				:nullable="false"
				class="mb-4"
				style="max-width: 20rem"
				@input="onLocaleChange"
			/>
		</div>

		<div class="col-12">
			<Table
				:loading="loading"
				:submitting="submitting"
				:cols="[
					{ key: 'key', heading: $t('translation-key'), size: 3 },
					{ key: 'original', heading: $t('original-text'), size: 5 },
					{ key: 'override', heading: $t('override-text'), size: 4 }
				]"
				:data="rows"
				breakpoint="md"
				:filters="{ ...filters }"
				:searchable="true"
				:scrollPaginate="true"
				@refresh="refresh"
				@toPage="toPage"
			>
				<template v-slot:col-override="{ item }">
					<input
						type="text"
						class="form-control"
						:value="item.overrideDraft"
						:placeholder="item.original"
						@input="onOverrideInput(item, $event.target.value)"
					/>
				</template>
			</Table>
		</div>
	</div>
</template>

<script>
import Table from "src/components/Table/Table.vue";

export default {
	components: {
		Table
	},
	data() {
		return {
			loading: true,
			submitting: false,
			dirty: false,
			locales: [],
			selectedLocale: null,
			allRows: [],
			filteredRows: [],
			rows: [],
			filters: {
				page: 1,
				page_size: 30,
				total: 0,
				search: ''
			}
		};
	},
	mounted() {
		this.locales = this.$i18n.availableLocales;
		this.selectedLocale = this.$i18n.locale && this.locales.includes(this.$i18n.locale) ? this.$i18n.locale : this.locales[0];
		this.loadLocale(this.selectedLocale);
	},
	methods: {
		loadBaseMessages(locale) {
			const context = require.context("../../locales", true, /\.json$/i);
			const fileKey = context.keys().find(key => key.includes(locale));
			return fileKey ? context(fileKey) : {};
		},
		loadLocale(locale) {
			this.loading = true;

			return this.$cfetch(`${this.$settings.APIBaseURL}/languageoverride/get`, {
				method: 'GET',
				data: { targetLocale: locale }
			})
				.then(res => {
					if (res.status !== 'SUCCESS') throw new Error('Unexpected error fetching language overrides');

					const base = this.loadBaseMessages(locale);
					const overrides = res.result.overrides;
					this.allRows = Object.keys(base).map(key => ({
						id: key,
						key,
						original: base[key],
						overrideDraft: Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== null ? String(overrides[key]) : ''
					}));

					this.dirty = false;
					this.filters = { ...this.filters, page: 1, search: '' };
					this.getList();
				})
				.catch(error => {
					this.$log.error.call(this, error);
					this.$store.commit('openDialog', {
						title: this.$t('something-went-wrong'),
						content: this.$t('error-fetching-language-overrides')
					});
				})
				.finally(() => {
					this.loading = false;
				});
		},
		onLocaleChange() {
			this.loadLocale(this.selectedLocale);
		},
		onOverrideInput(item, value) {
			item.overrideDraft = value;
			this.dirty = true;
		},
		getList() {
			const term = (this.filters.search || '').trim().toLowerCase();

			this.filteredRows = term
				? this.allRows.filter(row => row.key.toLowerCase().includes(term) || row.original.toLowerCase().includes(term))
				: this.allRows;

			this.filters.total = this.filteredRows.length;

			const start = (this.filters.page - 1) * this.filters.page_size;
			this.rows = this.filteredRows.slice(start, start + this.filters.page_size);
		},
		refresh(newFilters) {
			this.filters = { ...this.filters, ...newFilters };
			this.getList();
		},
		toPage(pageNo) {
			this.filters.page = pageNo;
			this.getList();
		},
		save() {
			if (this.submitting) return;
			this.submitting = true;

			this.$store.commit('showBlockingSpinner');

			const overrides = {};
			this.allRows.forEach(row => {
				const trimmed = (row.overrideDraft || '').trim();
				if (trimmed !== '') overrides[row.key] = row.overrideDraft;
			});

			this.$cfetch(`${this.$settings.APIBaseURL}/languageoverride/save`, {
				method: 'post',
				data: {
					targetLocale: this.selectedLocale,
					overrides
				}
			})
				.then(res => {
					if (res.status !== 'SUCCESS') throw new Error('Unexpected error saving language overrides');
					this.dirty = false;
					return this.$config.getConfig.call(this);
				})
				.then(() => {
					this.$store.commit('openDialog', {
						title: this.$t('success'),
						content: this.$t('language-overrides-saved-refresh')
					});
				})
				.catch(error => {
					this.$log.error.call(this, error);
					this.$store.commit('openDialog', {
						title: this.$t('something-went-wrong'),
						content: this.$t('error-saving-language-overrides')
					});
				})
				.finally(() => {
					this.$store.commit('hideBlockingSpinner');
					this.submitting = false;
				});
		}
	}
};
</script>
